import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Save, ToggleOn } from '@mui/icons-material';
import adminService from '../services/adminService';

const ROLE_LABELS = {
  client: 'Property Owners / Clients',
  contractor: 'Contractors',
  verifier: 'Verifiers',
  quality_inspector: 'Quality Inspectors',
  project_manager: 'Project Managers',
  merchant: 'Suppliers / Merchants',
  realtor: 'Realtors / Estate Agents',
  conveyancer: 'Conveyancers',
};

const AdminSignupSettingsPage = () => {
  const [rows, setRows] = useState([]);
  const [drafts, setDrafts] = useState({}); // role -> message draft
  const [loading, setLoading] = useState(true);
  const [savingRole, setSavingRole] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await adminService.getSignupSettings();
      setRows(data);
      setDrafts(Object.fromEntries(data.map((r) => [r.role, r.paused_message || ''])));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load signup settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const patch = async (role, changes) => {
    setSavingRole(role);
    setError('');
    setSuccess('');
    try {
      const res = await adminService.updateSignupSetting(role, changes);
      setSuccess(res.message || 'Saved');
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save');
      await load(); // resync UI with server truth
    } finally {
      setSavingRole('');
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 6, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <ToggleOn color="primary" />
        <Typography variant="h4">Sign-up Controls</Typography>
      </Stack>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Turn new sign-ups on or off per role, and edit the message shown to people who try to sign up while a role is paused.
        Invited users (e.g. invited contractors) are never blocked.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Stack spacing={2}>
        {rows.map((row) => {
          const open = Boolean(row.is_open);
          const dirty = (drafts[row.role] || '') !== (row.paused_message || '');
          return (
            <Card key={row.role} variant="outlined">
              <CardContent>
                <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap>
                  <Box>
                    <Typography variant="h6">{ROLE_LABELS[row.role] || row.role}</Typography>
                    {row.updated_at && (
                      <Typography variant="caption" color="text.secondary">
                        Last changed {new Date(row.updated_at).toLocaleString()}{row.updated_by_email ? ` by ${row.updated_by_email}` : ''}
                      </Typography>
                    )}
                  </Box>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Chip
                      size="small"
                      label={open ? 'Open' : 'Paused'}
                      color={open ? 'success' : 'warning'}
                    />
                    <FormControlLabel
                      control={(
                        <Switch
                          checked={open}
                          disabled={savingRole === row.role}
                          onChange={(e) => patch(row.role, { isOpen: e.target.checked })}
                        />
                      )}
                      label={open ? 'Accepting sign-ups' : 'Sign-ups paused'}
                    />
                  </Stack>
                </Stack>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" gutterBottom>
                  Message shown when paused
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  value={drafts[row.role] ?? ''}
                  onChange={(e) => setDrafts((prev) => ({ ...prev, [row.role]: e.target.value }))}
                  placeholder="Message shown to people who try to sign up while this role is paused"
                  inputProps={{ maxLength: 1000 }}
                  helperText={!open ? 'This is currently being shown to people trying to sign up.' : 'Shown only while this role is paused.'}
                />
                <Box sx={{ mt: 1, textAlign: 'right' }}>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<Save />}
                    disabled={!dirty || savingRole === row.role}
                    onClick={() => patch(row.role, { pausedMessage: drafts[row.role] })}
                  >
                    Save message
                  </Button>
                </Box>
              </CardContent>
            </Card>
          );
        })}
      </Stack>
    </Container>
  );
};

export default AdminSignupSettingsPage;
